import { Box, Stack, Typography } from '@healthinal/ui';
import { AddressContactDto, AddressDto, PatientAddressDto } from '../../../api/generated/model';
import { SyncListAction } from '../../../components/SyncListAction.tsx';
import { TypographyLink } from '../../../components/TypographyLink.tsx';
import { getValidUrlForLink } from '../../../utils/url.ts';

export function PatientAddressDisplay({
  flex,
  patientAddress,
  showAddressSyncIcon,
  onAddressSyncClick,
  addressSyncLoading,
  showContactSyncIcon,
  onContactSyncClick,
  contactSyncLoading,
}: {
  flex?: number;
  patientAddress: PatientAddressDto;
  showAddressSyncIcon?: boolean;
  onAddressSyncClick?: () => void;
  addressSyncLoading?: boolean;
  showContactSyncIcon?: boolean;
  onContactSyncClick?: () => void;
  contactSyncLoading?: boolean;
}) {
  return (
    <Stack flex={flex ?? 'none'} gap={1}>
      <AddressDisplay
        address={patientAddress.address}
        showSyncIcon={showAddressSyncIcon}
        onSyncClick={onAddressSyncClick}
        syncLoading={addressSyncLoading}
      />

      <ContactDisplay
        contact={patientAddress.contact}
        showSyncIcon={showContactSyncIcon}
        onSyncClick={onContactSyncClick}
        syncLoading={contactSyncLoading}
      />
    </Stack>
  );
}

export function AddressDisplay({
  address,
  showSyncIcon = false,
  onSyncClick,
  syncLoading = false,
}: {
  address: AddressDto;
  showSyncIcon?: boolean;
  onSyncClick?: () => void;
  syncLoading?: boolean;
}) {
  const homepageWithHttps = address?.homepage && getValidUrlForLink(address.homepage);
  const additionalInformation = getAddressAdditionalInformation(address);

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ width: '100%' }}>
      <Stack flex={1}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
          <Typography level="title-sm">{address.organization}</Typography>

          {showSyncIcon && onSyncClick && (
            <SyncListAction onClick={onSyncClick} loading={syncLoading} title="Adresse synchronisieren" />
          )}
        </Box>

        {address.address && <Typography level="body-sm">{address.address}</Typography>}

        {address.zip && <Typography level="body-sm">{address.zip}</Typography>}

        {address.city && <Typography level="body-sm">{address.city}</Typography>}

        {additionalInformation && <Typography level="body-sm">{additionalInformation}</Typography>}

        {address.email && (
          <TypographyLink href={`mailto:${address.email}`} level="body-sm">
            {address.email}
          </TypographyLink>
        )}

        {address.phone && (
          <TypographyLink href={`tel:${address.phone}`} level="body-sm">
            {address.phone}
          </TypographyLink>
        )}

        {address.homepage && (
          <TypographyLink href={homepageWithHttps ?? ''} level="body-sm" target="_blank">
            {address.homepage}
          </TypographyLink>
        )}
      </Stack>
    </Stack>
  );
}

function getAddressAdditionalInformation(address: AddressDto) {
  return address.additionalInformation ?? (address as AddressDto & { zusatz?: string | null }).zusatz;
}

export function ContactDisplay({
  contact,
  showNameAsTitle,
  showSyncIcon = false,
  onSyncClick,
  syncLoading = false,
}: {
  contact: AddressContactDto | undefined;
  showNameAsTitle?: boolean;
  showSyncIcon?: boolean;
  onSyncClick?: () => void;
  syncLoading?: boolean;
}) {
  if (!contact) return undefined;

  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');

  return (
    <Stack>
      <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
        {name && <Typography level={showNameAsTitle ? 'title-sm' : 'body-sm'}>{name}</Typography>}

        {showSyncIcon && onSyncClick && (
          <SyncListAction onClick={onSyncClick} loading={syncLoading} title="Kontakt synchronisieren" />
        )}
      </Box>

      {contact.function && <Typography level="body-sm">{contact.function}</Typography>}

      {contact.email && (
        <TypographyLink href={`mailto:${contact.email}`} level="body-sm">
          {contact.email}
        </TypographyLink>
      )}

      {contact.phone && (
        <TypographyLink href={`tel:${contact.phone}`} level="body-sm">
          {contact.phone}
        </TypographyLink>
      )}

      {/* {contact.address && <AddressDisplay address={contact.address} />} */}
    </Stack>
  );
}
